const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isOrgMasterId = (value) => UUID_RE.test(String(value || '').trim());

/** @param {Array<string|{id?: string, name?: string, designation_id?: string, department_id?: string}>} items */
export const normalizeOrgMasterOptions = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (typeof item === 'string') {
        const name = item.trim();
        return name ? { id: name, name } : null;
      }
      const id = item?.id || item?.designation_id || item?.department_id;
      const name = item?.name;
      if (!id || !name) return null;
      return { id: String(id), name: String(name) };
    })
    .filter(Boolean);
};

export const orgMasterOptionId = (option) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option.id || option.name || '';
};

export const orgMasterOptionLabel = (option) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option.name || option.id || '';
};

/** Filter/select value for a person row (master id when present). */
export const personOrgMasterFilterValue = (person, field) => {
  if (!person) return '';
  if (field === 'designation') {
    const raw = person.designation_id || person.designation || '';
    return isOrgMasterId(raw) ? raw : (person.designation_id || person.designation || '');
  }
  const raw = person.department_id || person.department || '';
  return isOrgMasterId(raw) ? raw : (person.department_id || person.department || '');
};

export const lookupOrgMasterNameById = (id, ...optionLists) => {
  const normalizedId = String(id || '').trim();
  if (!normalizedId) return null;
  for (const list of optionLists) {
    const options = normalizeOrgMasterOptions(list);
    const match = options.find((option) => option.id === normalizedId);
    if (match) return match.name;
  }
  return null;
};

/**
 * Display label for designation/department on a person row (never returns a raw UUID).
 * @param {'designation'|'department'} field
 */
export const resolvePersonOrgMasterDisplay = (person, field, ...optionLists) => {
  if (!person) return null;
  const idKey = field === 'designation' ? 'designation_id' : 'department_id';
  const nameKey = field;
  const rawName = person[nameKey];
  let id = person[idKey] || null;
  if (!id && isOrgMasterId(rawName)) {
    id = String(rawName).trim();
  }
  if (id) {
    const fromMasters = lookupOrgMasterNameById(id, ...optionLists);
    if (fromMasters) return fromMasters;
  }
  if (rawName && !isOrgMasterId(rawName)) {
    const trimmed = String(rawName).trim();
    return trimmed || null;
  }
  return null;
};

/** Label for MUI Select when value is a master id (avoids flashing raw UUID). */
export const orgMasterSelectDisplayLabel = (selectedId, options, person, field) => {
  const id = String(selectedId || '').trim();
  if (!id) return '';
  const match = normalizeOrgMasterOptions(options).find((option) => option.id === id);
  if (match?.name) return match.name;
  const fromPerson = resolvePersonOrgMasterDisplay(person, field, options);
  if (fromPerson) return fromPerson;
  return isOrgMasterId(id) ? '' : id;
};

/** Placeholder option until master list fetch completes. */
export const orgMasterPlaceholderOption = (selectedId, options, person, field) => {
  const id = String(selectedId || '').trim();
  if (!id) return null;
  const label = orgMasterSelectDisplayLabel(id, options, person, field);
  return { id, name: label || id };
};
